<template>
    <BaseCard padding="lg" class="flex flex-col gap-4">
        <div>
            <h2 class="body-semibold text-valence-stone-900">Your {{ cycle.template.name }} is collecting</h2>
            <div class="mt-3">
                <div class="progress-track">
                    <div class="progress-fill" :style="{ width: `${progressPct}%` }"></div>
                </div>
                <div class="flex items-center justify-between mt-2">
                    <span class="details text-valence-grey-750">{{ responsesLabel }}</span>
                    <span v-if="cycle.closes_at" class="details text-valence-grey-750">Closes {{ formatDate(cycle.closes_at, "short") }}</span>
                </div>
            </div>
        </div>

        <hr class="border-t border-valence-cream-200" />

        <div v-if="cycle.self_assess_enabled" class="self-assess-row">
            <div class="flex-1 min-w-0">
                <div class="body-semibold text-valence-stone-900">Your self reflection</div>
                <div v-if="!selfSubmitted" class="body-small text-valence-stone-500">
                    Rate yourself so we can show you the gap between how you see yourself and how others see you.
                </div>
            </div>
            <StatusChip v-if="selfSubmitted" state="done" size="2xs" class="w-fit">Complete</StatusChip>
            <button v-else type="button" class="base-btn-neutral base-btn-square base-btn-sm whitespace-nowrap" @click="onOpenSelf">Open</button>
        </div>

        <hr v-if="cycle.self_assess_enabled" class="border-t border-valence-cream-200" />

        <ul class="flex flex-col divide-y divide-valence-cream-200 -my-3">
            <li v-for="invite in visibleInvites" :key="invite.id" class="invite-row">
                <div class="invite-identity">
                    <BaseAvatar theme="identity" size="sm" :initials="getFirstLastInitials(invite.rater.display_name)" :identity-key="invite.rater.display_name" />
                    <div class="flex-1 min-w-0 flex flex-col">
                        <div class="body-serif text-valence-stone-900 truncate">{{ invite.rater.display_name }}</div>
                        <div class="scaffold-details-small text-valence-stone-500 truncate">{{ invite.rater.job_title || invite.rater.email }}</div>
                    </div>
                </div>
                <div class="invite-actions">
                    <BaseTooltip v-if="invite.invite_token" as-child theme="dark">
                        <template #trigger>
                            <button
                                type="button"
                                class="icon-btn"
                                :aria-label="`Copy invite link for ${invite.rater.display_name}`"
                                @click="onCopyLink(invite.invite_token, invite.id)">
                                <i class="bi" :class="copyStatus[invite.id] === 'Copied' ? 'bi-check2' : 'bi-link-45deg'" aria-hidden="true"></i>
                            </button>
                        </template>
                        <template #content>{{ copyStatus[invite.id] || "Copy invite link" }}</template>
                    </BaseTooltip>
                    <BaseTooltip v-if="canActOnInvite(cycle, invite)" as-child theme="dark">
                        <template #trigger>
                            <button
                                type="button"
                                class="icon-btn"
                                :aria-label="`Resend invite to ${invite.rater.display_name}`"
                                :disabled="reminding[invite.id]"
                                @click="onRemind(invite.id)">
                                <i class="bi" :class="reminding[invite.id] ? 'bi-check2' : 'bi-send'" aria-hidden="true"></i>
                            </button>
                        </template>
                        <template #content>{{ reminding[invite.id] ? "Sent" : "Resend invite" }}</template>
                    </BaseTooltip>
                    <StatusChip v-if="invite.state" :state="invite.state === 'responded' ? 'done' : 'pending'" size="2xs">
                        {{ invite.state === "responded" ? "Submitted" : "Pending" }}
                    </StatusChip>
                </div>
            </li>
        </ul>

        <hr class="border-t border-valence-cream-200" />

        <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-2">
                <a
                    :href="`/reflections/360/${cycle.id}/nominate`"
                    class="base-btn-neutral base-btn-square base-btn-sm whitespace-nowrap"
                    @click.prevent="emit('invite-more', cycle.id)">
                    Invite more raters
                </a>
                <BaseDropdownMenu :sections="menuSections" align="start" :side-offset="4" @select="handleMenuSelect">
                    <template #trigger>
                        <button type="button" class="more-btn" aria-label="More actions">
                            <i class="bi bi-three-dots" aria-hidden="true"></i>
                        </button>
                    </template>
                </BaseDropdownMenu>
            </div>
            <BaseTooltip as-child theme="dark" content-class="max-w-72 text-left">
                <template #trigger>
                    <button type="button" class="base-btn-dark base-btn-sm whitespace-nowrap" :disabled="!canWrapUp" @click="onWrapUp">Wrap Up</button>
                </template>
                <template #content>{{ canWrapUp ? "Close this 360 and generate the report" : wrapUpBlockedReason }}</template>
            </BaseTooltip>
        </div>
        <BaseNotification v-if="bulkReminderStatus" :theme="bulkReminderStatus.theme" :text="bulkReminderStatus.text" @dismiss="bulkReminderStatus = null" />

        <ConfirmationDialog
            v-model:open="showBulkReminderConfirm"
            :title="`Send a reminder to ${pendingCount} ${pendingCount === 1 ? 'rater' : 'raters'}?`"
            confirm-label="Send reminders"
            cancel-label="Cancel"
            :confirm-disabled="bulkReminderSending"
            :close-on-confirm="false"
            @confirm="onConfirmBulkRemind">
            <template #content>
                <div class="body text-valence-grey-750">
                    Each pending rater gets another email. <strong class="text-valence-grey-850">Raters emailed in the last 24 hours are skipped.</strong>
                </div>
            </template>
        </ConfirmationDialog>

        <BaseDialog v-model:open="showDeleteConfirm" title="Delete this 360?" dialog-content-class="md:max-w-[420px]">
            <div class="flex flex-col gap-6 px-8 pt-8 pb-2 md:pb-8">
                <div class="flex flex-col gap-2">
                    <h2 class="display-serif text-valence-stone-900">Delete this 360?</h2>
                    <p class="body text-valence-stone-500">
                        <strong class="body-bold text-valence-stone-800">This action can not be undone.</strong>
                        Your raters' invites and any feedback collected so far will be removed.
                    </p>
                </div>
                <div class="flex flex-col gap-1">
                    <button type="button" class="base-btn-brick w-full" :disabled="deleting" @click="onConfirmDelete">
                        {{ deleting ? "Deleting…" : "Delete 360" }}
                    </button>
                    <button type="button" class="base-btn-quiet w-full" @click="showDeleteConfirm = false">Cancel</button>
                </div>
            </div>
        </BaseDialog>
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleForSubject, RemindersSentResponse } from "~vue/api/generated/reflections";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { reportShortfallCopy, submittedProgressLabel, submittedProgressPct } from "~vue/utils/categoryThresholds";
import { getFirstLastInitials } from "~vue/utils/stringUtils";
import { canActOnInvite, formatDate } from "~vue/utils/threeSixty";
import { computed, onUnmounted, reactive, ref } from "vue";

interface Props {
    cycle: CycleForSubject;
}

const { cycle } = defineProps<Props>();
const emit = defineEmits<{
    (event: "invite-more", cycleId: string): void;
    (event: "remind", inviteId: string): void;
    (event: "wrap-up", cycleId: string): void;
    (event: "open-self", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "deleted", cycleId: string): void;
}>();

const copyStatus = reactive<Record<string, string>>({});
const reminding = reactive<Record<string, boolean>>({});
// Track pending clear timers so we can cancel them on unmount and avoid
// touching state on a dead component (or growing the dicts unbounded across a
// long session).
const clearTimers = new Set<ReturnType<typeof setTimeout>>();
onUnmounted(() => {
    for (const id of clearTimers) {
        clearTimeout(id);
    }
    clearTimers.clear();
});

const selfSubmitted = computed(() => Boolean(cycle.self_response_submitted_at));

// Revoked/expired invites are technically still on the cycle but shouldn't appear
// in the subject-facing rater list. An organization that hides rater status sends
// no per-invite state, and has already dropped those invites server-side.
const visibleInvites = computed(() => (cycle.rater_status_hidden ? cycle.invites : cycle.invites.filter((i) => i.state === "pending" || i.state === "responded")));

const thresholds = computed(() => cycle.category_thresholds ?? null);

const responsesLabel = computed(() => {
    if (thresholds.value !== null) {
        return `${cycle.response_summary.responded} responses recorded · ${submittedProgressLabel(thresholds.value)}`;
    }
    return `${cycle.response_summary.responded} of ${Math.max(cycle.response_summary.total, cycle.min_responses)} responses recorded`;
});

const progressPct = computed(() => {
    if (thresholds.value !== null) {
        return submittedProgressPct(thresholds.value);
    }
    const denom = Math.max(cycle.min_responses, cycle.response_summary.total);
    if (!denom) {
        return 0;
    }
    return Math.min(100, Math.round((cycle.response_summary.responded / denom) * 100));
});

const pendingCount = computed(() => (cycle.rater_status_hidden ? cycle.response_summary.pending : visibleInvites.value.filter((i) => i.state === "pending").length));
const hasPending = computed(() => pendingCount.value > 0);

const showBulkReminderConfirm = ref(false);
const bulkReminderSending = ref(false);
const bulkReminderStatus = ref<{ theme: "success" | "error" | "info"; text: string } | null>(null);

// Anonymity floor: manual wrap-up is only allowed once at least 3 raters have
// submitted feedback so no rater's response can be back-inferred. Independent
// of the admin-configured `min_responses` (which controls auto-unlock and can
// be set to any value ≥ 0).
const MIN_RESPONSES_TO_WRAP_UP = 3;
// Closing early can't hand over a report the thresholds would still withhold,
// so a threshold cycle has to clear them too.
const canWrapUp = computed(() => cycle.response_summary.responded >= MIN_RESPONSES_TO_WRAP_UP && (thresholds.value === null || thresholds.value.eligible_for_report));

const wrapUpBlockedReason = computed(() => {
    const shortfall = thresholds.value === null ? null : reportShortfallCopy(thresholds.value);
    if (shortfall !== null) {
        return shortfall;
    }
    return `You need at least ${MIN_RESPONSES_TO_WRAP_UP} responses to close early`;
});

function scheduleClear(fn: () => void, ms: number) {
    const id = setTimeout(() => {
        clearTimers.delete(id);
        fn();
    }, ms);
    clearTimers.add(id);
}

async function onCopyLink(inviteToken: string, inviteId: string) {
    const url = `${globalThis.location.origin}/reflections/360/r/${inviteToken}/`;
    try {
        await navigator.clipboard.writeText(url);
        copyStatus[inviteId] = "Copied";
        scheduleClear(() => delete copyStatus[inviteId], 2000);
    } catch {
        copyStatus[inviteId] = "Copy failed";
    }
}

function onRemind(inviteId: string) {
    reminding[inviteId] = true;
    emit("remind", inviteId);
    // Parent handles the fetch + toast; we reset the flag conservatively so
    // repeat clicks re-enable after a short guard window.
    scheduleClear(() => {
        reminding[inviteId] = false;
    }, 2000);
}

// One server-side call decides which raters are still pending, so the batch
// reminder works for an organization that hides individual rater status. It reports
// no counts back — how many were emailed is itself rater status.
async function onConfirmBulkRemind() {
    if (bulkReminderSending.value) {
        return;
    }
    bulkReminderSending.value = true;
    bulkReminderStatus.value = null;
    const { data, error } = await useFetch(`/v1/reflections/cycles/${cycle.id}/reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json<RemindersSentResponse>();
    bulkReminderSending.value = false;
    showBulkReminderConfirm.value = false;
    if (error.value || !data.value) {
        bulkReminderStatus.value = { theme: "error", text: "Could not send reminders. Please try again." };
        return;
    }
    bulkReminderStatus.value = {
        theme: "success",
        text: "Reminders sent to everyone still pending. Anyone emailed in the last 24 hours is skipped until tomorrow.",
    };
}

const showDeleteConfirm = ref(false);
const deleting = ref(false);

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: [
            {
                id: "send-batch-reminder",
                type: "button",
                label: bulkReminderSending.value ? "Sending…" : "Send batch reminder",
                disabled: !hasPending.value || bulkReminderSending.value,
            },
            { id: "delete", type: "button", label: "Delete 360", theme: "danger", disabled: deleting.value },
        ],
    },
]);

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "send-batch-reminder") {
        showBulkReminderConfirm.value = true;
    } else if (item.id === "delete") {
        showDeleteConfirm.value = true;
    }
};

async function onConfirmDelete() {
    if (deleting.value) {
        return;
    }
    deleting.value = true;
    const { error } = await useFetch(`/v1/reflections/cycles/${cycle.id}`).delete();
    deleting.value = false;
    showDeleteConfirm.value = false;
    if (error.value) {
        bulkReminderStatus.value = { theme: "error", text: "Could not delete this 360. Please try again." };
        return;
    }
    emit("deleted", cycle.id);
}

function onWrapUp() {
    emit("wrap-up", cycle.id);
}

function onOpenSelf() {
    emit("open-self", { cycleId: cycle.id, title: cycle.template.name, closesAt: cycle.closes_at ?? null });
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .progress-track {
        @apply w-full h-2 bg-valence-cream-100 rounded-full overflow-hidden;
    }

    .progress-fill {
        @apply h-full bg-valence-pink-600 rounded-full transition-[width];
    }

    .icon-btn {
        @apply inline-flex items-center justify-center w-7 h-7 rounded-lg text-valence-stone-500 hover:text-valence-stone-900 hover:bg-valence-cream-50 disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-valence-primary-300;
    }

    /* Stack identity + actions on mobile so the name/role has full width; on sm+ the row
       is a single flex line matching the desktop layout. */
    .invite-row {
        @apply flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-3;
    }

    .invite-identity {
        @apply flex flex-1 min-w-0 items-center gap-3;
    }

    .invite-actions {
        /* On mobile the actions sit under the identity row, indented past the avatar
           (w-8 = 32px avatar + gap-3 = 12px) so they visually align with the name. */
        @apply flex items-center gap-2 pl-11 sm:pl-0;
    }

    .more-btn {
        @apply inline-flex items-center justify-center w-9 h-8 rounded-lg bg-white border border-valence-cream-300 text-valence-stone-900 hover:bg-valence-cream-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-valence-primary-300;
    }

    .self-assess-row {
        @apply flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3;
    }
}
</style>
